import { useEffect, useState } from 'react';
import { ReviewLog, ReviewLogListResponse } from '@/types/reviewLog';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ReviewLogModal } from '@/components/ReviewLogModal';
import { Plus, Edit, Calendar, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

export function ReviewLogs() {
    const [logs, setLogs] = useState<ReviewLog[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedLog, setSelectedLog] = useState<ReviewLog | null>(null);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const res: ReviewLogListResponse = await api.getReviewLogs({ page, limit: 10 });
            setLogs(res.data);
            setTotal(res.pagination.total);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [page]);

    const handleCreate = () => {
        setSelectedLog(null);
        setIsModalOpen(true);
    };

    const handleEdit = (log: ReviewLog) => {
        setSelectedLog(log);
        setIsModalOpen(true);
    };

    const handleSave = () => {
        fetchLogs();
    };

    return (
        <div className="p-6 max-w-5xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold tracking-tight">Review Logs</h1>
                <Button onClick={handleCreate}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create
                </Button>
            </div>

            <div className="space-y-4">
                {logs.map(log => (
                    <div
                        key={log.id}
                        className="group flex flex-col gap-2 p-3 border rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-900/50 transition-colors cursor-pointer bg-card text-card-foreground shadow-sm"
                        onDoubleClick={() => handleEdit(log)}
                    >
                        <div className="flex items-center justify-between text-sm text-muted-foreground mb-1">
                            <div className="flex items-center gap-4">
                                <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {new Date(log.review_date).toLocaleDateString()}
                                </span>
                                <span className="flex items-center gap-1 font-mono text-zinc-700 dark:text-zinc-300">
                                    <User className="h-3 w-3" />
                                    {log.entity_code}
                                </span>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(log);
                                }}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="text-sm text-foreground/80">
                            {log.abstraction ? (
                                <div className="prose dark:prose-invert prose-sm max-w-none prose-p:m-0 prose-headings:m-0 prose-ul:m-0 prose-ol:m-0 prose-li:m-0">
                                    <ReactMarkdown>{log.abstraction}</ReactMarkdown>
                                </div>
                            ) : (
                                <span className="text-muted-foreground italic">No abstraction</span>
                            )}
                        </div>
                    </div>
                ))}

                {logs.length === 0 && !loading && (
                    <div className="text-center py-12 text-muted-foreground border rounded-lg border-dashed">
                        No review logs found. Create one to get started.
                    </div>
                )}
            </div>

            {/* Pagination */}
            {total > 10 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                    <Button
                        variant="outline"
                        disabled={page === 1}
                        onClick={() => setPage(p => p - 1)}
                    >
                        Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                        Page {page} of {Math.ceil(total / 10)}
                    </span>
                    <Button
                        variant="outline"
                        disabled={page * 10 >= total}
                        onClick={() => setPage(p => p + 1)}
                    >
                        Next
                    </Button>
                </div>
            )}

            <ReviewLogModal
                open={isModalOpen}
                onOpenChange={setIsModalOpen}
                reviewLog={selectedLog}
                onSave={handleSave}
            />
        </div>
    );
}
